import { CloseOutlined, SaveFilled } from "@ant-design/icons"
import {
  Button,
  Collapse,
  Empty,
  Flex,
  Form,
  type FormInstance,
  Space,
  Switch,
  Tooltip,
  Typography
} from "antd"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { FormChangedWatcher } from "../../../components/Forms/FormChangedWatcher/FormChangedWatcher"
import {
  CHANNEL_KEYS,
  CRITICAL_GROUPS
} from "../constants/notifications-settings"
import type {
  NotificationSettings,
  NotificationSettingsItem
} from "../types/notification.types"

const { Text } = Typography

type Channel = "email" | "in_app" | "phone" | "desktop"
type ChannelValues = Record<Channel, boolean>

const CHANNELS = CHANNEL_KEYS as Channel[]

// When a bulk "off" would empty a critical row, one channel is kept on. This is
// the order it prefers among the channels currently enabled on that row.
const KEEP_PRIORITY: Channel[] = ["in_app", "email", "phone", "desktop"]

function enabledCount(row: ChannelValues) {
  return CHANNELS.filter((c) => row[c]).length
}

// Value a single channel takes when a bulk "off" sweeps it: false, unless that
// would leave a critical row with nothing on — then it stays.
function clearedChannel(
  row: ChannelValues,
  channel: Channel,
  isCriticalGroup: boolean
): boolean {
  if (isCriticalGroup && row[channel] && enabledCount(row) === 1) return true
  return false
}

// Whole-row value after a bulk "off". A critical row keeps its highest-priority
// still-enabled channel so finance alerts never go fully dark.
function clearedRow(row: ChannelValues, isCriticalGroup: boolean): ChannelValues {
  if (!isCriticalGroup || enabledCount(row) === 0) {
    return Object.fromEntries(
      CHANNELS.map((c) => [c, false])
    ) as ChannelValues
  }
  const keep = KEEP_PRIORITY.find((c) => row[c]) ?? "in_app"
  return Object.fromEntries(
    CHANNELS.map((c) => [c, c === keep])
  ) as ChannelValues
}

function useGroupRows(
  items: NotificationSettingsItem[],
  form: FormInstance
): Record<number, ChannelValues> {
  const watched = Form.useWatch([], form) as
    | Record<number, Partial<ChannelValues>>
    | undefined

  return Object.fromEntries(
    items.map((item) => {
      const w = watched?.[item.notification_type]
      return [
        item.notification_type,
        Object.fromEntries(
          CHANNELS.map((c) => [c, w?.[c] ?? item[c]])
        ) as ChannelValues
      ]
    })
  )
}

interface BulkSwitchProps {
  groupKey: string
  items: NotificationSettingsItem[]
  isCriticalGroup: boolean
  form: FormInstance
}

function SectionMasterSwitch({
  groupKey,
  items,
  isCriticalGroup,
  form
}: BulkSwitchProps) {
  const { t } = useTranslation()
  const rows = useGroupRows(items, form)
  const values = items.map((it) => rows[it.notification_type])

  const allOn = values.every((r) => CHANNELS.every((c) => r[c]))
  const someOn = values.some((r) => CHANNELS.some((c) => r[c]))
  const partiallyOn = someOn && !allOn

  const handleChange = (next: boolean) => {
    const patch: Record<number, ChannelValues> = {}
    items.forEach((it) => {
      patch[it.notification_type] = next
        ? (Object.fromEntries(CHANNELS.map((c) => [c, true])) as ChannelValues)
        : clearedRow(rows[it.notification_type], isCriticalGroup)
    })
    form.setFieldsValue(patch)
  }

  return (
    <span
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <Tooltip
        title={
          partiallyOn
            ? t("notifications.settings.sectionPartiallyOn")
            : undefined
        }
      >
        <Switch
          size="small"
          checked={allOn}
          onChange={handleChange}
          className={partiallyOn ? "opacity-60" : undefined}
          data-testid={`bulk-toggle-${groupKey}-all`}
          aria-label={t("notifications.settings.toggleSection", {
            group: t(`notifications.groups.${groupKey}`, {
              defaultValue: groupKey
            })
          })}
        />
      </Tooltip>
    </span>
  )
}

function ChannelColumnSwitch({
  groupKey,
  channel,
  items,
  isCriticalGroup,
  form
}: BulkSwitchProps & { channel: Channel }) {
  const { t } = useTranslation()
  const rows = useGroupRows(items, form)
  const values = items.map((it) => rows[it.notification_type])

  const allOn = values.every((r) => r[channel])
  const someOn = values.some((r) => r[channel])
  const partiallyOn = someOn && !allOn

  const handleChange = (next: boolean) => {
    const patch: Record<number, ChannelValues> = {}
    items.forEach((it) => {
      const row = rows[it.notification_type]
      patch[it.notification_type] = {
        ...row,
        [channel]: next ? true : clearedChannel(row, channel, isCriticalGroup)
      }
    })
    form.setFieldsValue(patch)
  }

  return (
    <Tooltip
      title={
        partiallyOn ? t("notifications.settings.sectionPartiallyOn") : undefined
      }
    >
      <Switch
        size="small"
        checked={allOn}
        onChange={handleChange}
        className={partiallyOn ? "opacity-60" : undefined}
        data-testid={`bulk-toggle-${groupKey}-${channel}`}
        aria-label={t("notifications.settings.toggleChannel", {
          channel: t(`notifications.channels.${channel}`),
          group: t(`notifications.groups.${groupKey}`, {
            defaultValue: groupKey
          })
        })}
      />
    </Tooltip>
  )
}

interface NotificationSettingRowProps {
  item: NotificationSettingsItem
  isCriticalGroup: boolean
  form: FormInstance
}

function NotificationSettingRow({
  item,
  isCriticalGroup,
  form
}: NotificationSettingRowProps) {
  const { t } = useTranslation()
  const row = Form.useWatch([item.notification_type], form) ?? item
  const enabledCount = CHANNEL_KEYS.filter((c) => row[c]).length

  return (
    <div
      className={"border-b border-border grid py-1.5 px-4 items-center"}
      style={{ gridTemplateColumns: `2fr repeat(${CHANNELS.length}, 1fr)` }}
    >
      <Text>{item.notification_type_name}</Text>
      {CHANNEL_KEYS.map((channel) => {
        const locked =
          isCriticalGroup && row[channel] === true && enabledCount === 1

        return (
          <Flex justify="center" key={channel}>
            <Tooltip
              title={
                locked ? t("notifications.settings.criticalLocked") : undefined
              }
            >
              <span>
                <Form.Item
                  name={[item.notification_type, channel]}
                  valuePropName="checked"
                  initialValue={item[channel]}
                  noStyle
                >
                  <Switch size="small" disabled={locked} />
                </Form.Item>
              </span>
            </Tooltip>
          </Flex>
        )
      })}
    </div>
  )
}

interface NotificationSettingsFormProps {
  settings: NotificationSettings
  onFinish: (values: { [key: string]: NotificationSettingsItem }) => void
  saving: boolean
}

export function NotificationSettingsForm({
  settings,
  onFinish,
  saving
}: NotificationSettingsFormProps) {
  const [form] = Form.useForm()
  const { t } = useTranslation()

  const [isDirty, setIsDirty] = useState(false)

  return (
    <div className={"flex-1 flex gap-3 flex-col w-full with-default-error"}>
      <Form
        form={form}
        layout="vertical"
        component={false}
        onFinish={onFinish}
        disabled={saving}
      >
        <FormChangedWatcher
          onDirtyChange={setIsDirty}
          form={form}
          saving={saving}
        />
        <div className={"flex-1"}>
          {settings ? (
            <Collapse
              className="border-none bg-transparent"
              defaultActiveKey={Object.keys(settings)}
              items={Object.entries(settings).map(([key, value]) => {
                const isCriticalGroup = CRITICAL_GROUPS.includes(key)

                return {
                  className:
                    "rounded-md border border-border shadow-sm overflow-hidden mb-2 h-fit",
                  key: key,
                  label: (
                    <Flex align="center" gap={8}>
                      <Text strong>
                        {t(`notifications.groups.${key}`, { defaultValue: key })}
                      </Text>
                      <SectionMasterSwitch
                        groupKey={key}
                        items={value}
                        isCriticalGroup={isCriticalGroup}
                        form={form}
                      />
                    </Flex>
                  ),
                  children: (
                    <div>
                      <div
                        className={
                          "py-1.5 px-4 grid border-b border-border bg-background"
                        }
                        style={{
                          gridTemplateColumns: `2fr repeat(${CHANNELS.length}, 1fr)`
                        }}
                      >
                        <div />
                        {CHANNELS.map((channel) => (
                          <div
                            key={channel}
                            className={"flex items-center justify-center"}
                          >
                            <span
                              className={"relative inline-flex items-center"}
                            >
                              <Text
                                type="secondary"
                                className={
                                  "absolute right-full mr-2 text-xs whitespace-nowrap"
                                }
                              >
                                {channel === "in_app" ? (
                                  <Tooltip
                                    title={t(
                                      "notifications.settings.inAppHint"
                                    )}
                                  >
                                    <span className="underline decoration-dotted cursor-help">
                                      {t(`notifications.channels.${channel}`)}
                                    </span>
                                  </Tooltip>
                                ) : (
                                  t(`notifications.channels.${channel}`)
                                )}
                              </Text>
                              <ChannelColumnSwitch
                                groupKey={key}
                                channel={channel}
                                items={value}
                                isCriticalGroup={isCriticalGroup}
                                form={form}
                              />
                            </span>
                          </div>
                        ))}
                      </div>
                      {value.map((item) => (
                        <NotificationSettingRow
                          key={item.notification_type}
                          item={item}
                          isCriticalGroup={isCriticalGroup}
                          form={form}
                        />
                      ))}
                    </div>
                  )
                }
              })}
            />
          ) : (
            <Empty description={t("notifications.settings.noSettings")} />
          )}
        </div>
        <Flex justify="end" className={"sticky bottom-0 bg-background py-2"}>
          <Space>
            <Button
              onClick={() => form.resetFields()}
              disabled={!isDirty || saving}
              icon={<CloseOutlined />}
              variant={"solid"}
            >
              {t("notifications.settings.cancelChanges")}
            </Button>
            <Button
              disabled={!isDirty || saving}
              loading={saving}
              onClick={form.submit}
              icon={<SaveFilled />}
              variant={"solid"}
              color={"primary"}
            >
              {t("notifications.settings.saveChanges")}
            </Button>
          </Space>
        </Flex>
      </Form>
    </div>
  )
}
